import { useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { UserActivity, SecurityEvent, SecurityAlert } from '../services/loggingApiService';

interface UseLoggingWebSocketReturn {
  isConnected: boolean;
  socket: Socket | null;
  lastActivity: UserActivity | null;
  lastSecurityEvent: SecurityEvent | null;
  lastAlert: SecurityAlert | null;
  connectionError: string | null;
}

/**
 * Custom hook for WebSocket connection to logging events
 * Provides real-time updates for user activities, security events, and alerts
 */
export const useLoggingWebSocket = (): UseLoggingWebSocketReturn => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastActivity, setLastActivity] = useState<UserActivity | null>(null);
  const [lastSecurityEvent, setLastSecurityEvent] = useState<SecurityEvent | null>(null);
  const [lastAlert, setLastAlert] = useState<SecurityAlert | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const connectSocket = useCallback(() => {
    try {
      // Get JWT token for authentication
      const token = localStorage.getItem('webelieve_access_token');
      
      if (!token) {
        setConnectionError('No authentication token found');
        return;
      }

      // Create socket connection to logging namespace
      const newSocket = io('/logging', {
        path: '/socket.io', // Match backend Socket.IO path configuration
        auth: { token },
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('[LoggingWebSocket] Connected to logging service');
        setIsConnected(true);
        setConnectionError(null);
        
        // Join realm-specific room for multi-tenant isolation
        // Note: Realm will be extracted from JWT on server side
        newSocket.emit('join-logging-room');
      });

      newSocket.on('disconnect', (reason) => {
        console.log('[LoggingWebSocket] Disconnected:', reason);
        setIsConnected(false);
        
        if (reason === 'io server disconnect') {
          // Server disconnected, need to reconnect manually
          setTimeout(() => newSocket.connect(), 1000);
        }
      });

      newSocket.on('connect_error', (error) => {
        console.error('[LoggingWebSocket] Connection error:', error);
        setConnectionError(error.message);
        setIsConnected(false);
      });

      // Real-time event handlers
      newSocket.on('new-activity', (activity: UserActivity) => {
        console.log('[LoggingWebSocket] New activity received:', activity);
        setLastActivity(activity);
      });

      newSocket.on('security-event', (event: SecurityEvent) => {
        console.log('[LoggingWebSocket] Security event received:', event);
        setLastSecurityEvent(event);
        
        // Log critical security events to console for immediate attention
        if (event.severity === 'critical') {
          console.warn('🚨 CRITICAL SECURITY EVENT:', event);
        }
      });

      newSocket.on('security-alert', (alert: SecurityAlert) => {
        console.log('[LoggingWebSocket] Security alert received:', alert);
        setLastAlert(alert);
        
        // Log critical alerts to console for immediate attention
        if (alert.priority === 'critical') {
          console.error('🚨 CRITICAL SECURITY ALERT:', alert);
        }
      });

      newSocket.on('critical-security-alert', (alert: SecurityAlert) => {
        console.error('[LoggingWebSocket] CRITICAL ALERT received:', alert);
        setLastAlert({ ...alert, priority: 'critical' });
        
        // Could trigger browser notification here
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`Critical Security Alert: ${alert.title}`, {
            body: alert.description,
            icon: '/favicon.ico',
            tag: `alert-${alert.id}`,
            requireInteraction: true
          });
        }
      });

      // Error event handlers
      newSocket.on('error', (error) => {
        console.error('[LoggingWebSocket] Socket error:', error);
        setConnectionError(error.message || 'Socket error occurred');
      });

      setSocket(newSocket);
      
      return newSocket;
    } catch (error) {
      console.error('[LoggingWebSocket] Failed to create socket:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect');
      return null;
    }
  }, []);

  // Initialize socket connection
  useEffect(() => {
    const newSocket = connectSocket();

    return () => {
      if (newSocket) {
        console.log('[LoggingWebSocket] Cleaning up socket connection');
        newSocket.disconnect();
      }
    };
  }, [connectSocket]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('[LoggingWebSocket] Notification permission:', permission);
      });
    }
  }, []);

  return {
    isConnected,
    socket,
    lastActivity,
    lastSecurityEvent,
    lastAlert,
    connectionError
  };
}; 